













































































































































import Vue from 'vue';
import { mapGetters } from 'vuex';
import {
  FETCH_ORG_PRICES,
  FETCH_ORG_PRODUCTS,
  FETCH_RECENT_SURVEY_RESPONSES,
  SCHEDULE_SAVE_REACTIVATION_CAMPAIGN_BLUEPRINT,
} from '@/store/action-types';
import { STORE_REACTIVATION_CAMPAIGN_BLUEPRINT_DATA } from '@/store/mutation-types';
import {
  ICustomAttribute,
  ISegmentFilter,
  IState,
  SegmentAttribute,
  SegmentOperand,
  ICommonABTest,
  ICampaignBlueprint,
  IAudience,
} from '@/store/types';
import VerticalLine from '@/components/UI/VerticalLine.vue';
import SegmentFilterEditor from '@/components/Builder/SegmentFilterEditor.vue';
import CustomAttributeEditor from '@/components/UI/CustomAttributeEditor.vue';
import {
  CUSTOM_ATTRIBUTE_TYPE,
  ENABLED_SEGMENT_ATTRIBUTES,
  getDefaultFilterValue,
  SAVE_STATE,
  segmentAttributeLabels,
  SEGMENT_ATTRIBUTE,
  SEGMENT_OPERAND,
  SEGMENT_ATTRIBUTE_TYPE,
} from '@/store/shared';

const attributeOptions = ENABLED_SEGMENT_ATTRIBUTES.REACTIVATION as string[];

interface IAttributeOption {
  label: string;
  value: string;
  custom?: boolean;
}

export default Vue.extend({
  name: 'AudienceEditor',
  components: {
    VerticalLine,
    SegmentFilterEditor,
    CustomAttributeEditor,
  },
  data() {
    return {
      pricesProductsLoading: true,
      showAddCustom: false,
      segmentAttributeLabels,
      attributeOptions,
    };
  },
  async created() {
    await this.$store.dispatch(FETCH_ORG_PRICES);
    await this.$store.dispatch(FETCH_ORG_PRODUCTS);
    await this.$store.dispatch(FETCH_RECENT_SURVEY_RESPONSES);
    this.pricesProductsLoading = false;
  },
  computed: {
    ...mapGetters({
      campaignBlueprint: 'currentReactivationCampaignBlueprint',
      customAttributesEnabled: 'reactivationCustomAttributesEnabled',
      abTestsEnabled: 'reactivationABTestsEnabled',
    }),
    loading(): boolean {
      return this.pricesProductsLoading || !this.campaignBlueprint;
    },
    audience(): IAudience | undefined {
      return this.campaignBlueprint?.audience;
    },
    abTest(): ICommonABTest | undefined {
      if (!this.abTestsEnabled) return undefined;
      return this.campaignBlueprint?.abTests?.find((abTest: ICommonABTest) => !abTest.completedAt);
    },
    filters(): ISegmentFilter[] {
      return this.audience?.filters || [];
    },
    customAttributes(): ICustomAttribute[] {
      if (!this.customAttributesEnabled) return [];
      const state = this.$store.state as IState;
      return state.org?.customAttributes || [];
    },
    combinedAttributeOptions(): IAttributeOption[] {
      // combine attribute options and customAttributes
      const attributeOptionsCombined = this.attributeOptions.map((attribute) => ({
        label: segmentAttributeLabels[attribute as SegmentAttribute],
        value: attribute,
      }));
      const customAttributes = this.customAttributes.map((attribute) => ({
        label: attribute.label,
        value: attribute.name,
        custom: true,
      }));
      return attributeOptionsCombined.concat(customAttributes);
    },
  },
  methods: {
    updateCampaignBlueprint(update: Partial<ICampaignBlueprint>) {
      if (this.campaignBlueprint) {
        const id = this.campaignBlueprint._id;
        this.$store.commit(STORE_REACTIVATION_CAMPAIGN_BLUEPRINT_DATA, { id, data: { saveState: SAVE_STATE.UNSAVED, ...update } });
        this.$store.dispatch(SCHEDULE_SAVE_REACTIVATION_CAMPAIGN_BLUEPRINT, { id });
      }
    },
    updateAudience(update: Partial<IAudience>) {
      if (this.audience) {
        this.updateCampaignBlueprint({
          audience: {
            ...this.audience,
            ...update,
          },
        });
      }
    },
    addFilter(attributeKey?: string) {
      let newFilter = {} as ISegmentFilter;
      // for both custom and default attributes
      if (attributeKey) {
        if (this.attributeOptions.includes(attributeKey)) {
          // default attribute
          const attributeType = SEGMENT_ATTRIBUTE_TYPE[attributeKey as SegmentAttribute];
          const operand = [
            SEGMENT_ATTRIBUTE.SUBSCRIPTION_START_DATE,
            SEGMENT_ATTRIBUTE.PRICE,
            SEGMENT_ATTRIBUTE.SUBSCRIPTION_AGE_MONTHS,
            SEGMENT_ATTRIBUTE.BILLING_INTERVAL_COUNT,
            SEGMENT_ATTRIBUTE.INVOICE_AMOUNT_DUE,
          ].includes(attributeKey as SegmentAttribute)
            ? SEGMENT_OPERAND.GTE
            : SEGMENT_OPERAND.INCLUDES;
          const newFilterValue = getDefaultFilterValue(attributeType, operand);
          newFilter = {
            attribute: attributeKey as SegmentAttribute,
            operand,
            value: newFilterValue,
          };
        } else {
          // custom attribute
          const match = this.customAttributes.find((attribute) => attribute.name === attributeKey);
          if (match) {
            let operand: SegmentOperand;
            if (match.type === CUSTOM_ATTRIBUTE_TYPE.NUMBER || match.type === CUSTOM_ATTRIBUTE_TYPE.DATE) {
              operand = SEGMENT_OPERAND.GTE;
            } else {
              operand = SEGMENT_OPERAND.INCLUDES;
            }
            const newFilterValue = getDefaultFilterValue(match.type, operand);
            newFilter = {
              attribute: attributeKey as SegmentAttribute,
              operand,
              value: newFilterValue,
              type: match.type,
            };
          }
        }
      }
      const audience = {
        name: '',
        ...this.audience,
        filters: [...this.filters, newFilter],
      };
      this.updateCampaignBlueprint({ audience });
    },
    removeFilter(index: number) {
      const audience = {
        name: '',
        ...this.audience,
        filters: this.filters.filter((_, i) => i !== index),
      };
      this.updateCampaignBlueprint({ audience });
    },
    updateFilterItem(index: number, data: ISegmentFilter) {
      const filters = [...this.filters];
      filters[index] = data;
      const audience = {
        name: '',
        ...this.audience,
        filters,
      };
      this.updateCampaignBlueprint({ audience });
    },
    nextStep() {
      this.$emit('next');
    },
  },
});
